import { ArrowLeft, ArrowRight, Building2, Database, Eye, EyeOff, Flower2, Glasses, Hand, House, Lock, MoreHorizontal, Phone, Scissors, Sparkles, Wrench } from 'lucide-react';
import axios from 'axios';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config/Api_base';
import { useAuthStore } from '../../stores/authStore';
import { ensureShopTypeTabDefault } from '../../stores/serviceTabsStore';
import styles from '../../assets/scss/pages/AdminLogin.module.scss';
import loginBg from '../../assets/img/poter_login.png';
import logo from '../../assets/img/logo.png';
import { version } from '../../config/version-code';

// Danh sách ngành nghề kinh doanh — key gửi xuống BE (shopType) để chọn bộ dữ liệu mẫu phù hợp.
// Lưu ý: ngành 'khác' được BE lưu shopType = 'kinhmat' (xử lý ở chỗ gọi handleRegister).
const BUSINESS_TYPES = [
    { key: 'kinhmat', label: 'Kính mắt', icon: Glasses },
    { key: 'spa', label: 'Spa', icon: Flower2 },
    { key: 'nails', label: 'Nails', icon: Hand },
    { key: 'salon', label: 'Salon', icon: Scissors },
    { key: 'vatlieu', label: 'Vật liệu - thiết bị', icon: Wrench },
    { key: 'khac', label: 'Khác', icon: MoreHorizontal },
];

// Dùng plain axios không qua interceptor để tránh gắn token cũ vào request đăng ký
const rawApi = axios.create({ baseURL: API_BASE, headers: { 'Content-Type': 'application/json' } });

const isValidVietnamPhone = (raw) => {
    const cleaned = (raw || '').replace(/\D/g, '');
    return /^(0)(3|5|7|8|9)[0-9]{8}$/.test(cleaned);
};


export default function RegisterPage() {
    const navigate = useNavigate();
    const login = useAuthStore((s) => s.login);

    const [step, setStep] = useState(1); // 1: phone+password, 2: shopName, 3: setup
    const [showIndustry, setShowIndustry] = useState(false); // bước con chọn ngành (khi có dữ liệu mẫu)
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [shopName, setShopName] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [bgLoaded, setBgLoaded] = useState(false);

    const cleanedPhone = phone.replace(/\D/g, '');

    const handleNextStep = (e) => {
        e.preventDefault();
        setError('');
        if (!isValidVietnamPhone(cleanedPhone)) {
            setError('Số điện thoại không hợp lệ (10 số, bắt đầu bằng 03, 05, 07, 08 hoặc 09)');
            return;
        }
        if (!password.trim()) {
            setError('Vui lòng nhập mật khẩu');
            return;
        }
        if (password.length < 6) {
            setError('Mật khẩu phải có ít nhất 6 ký tự');
            return;
        }
        setStep(2);
    };

    const handleGoStep3 = (e) => {
        e.preventDefault();
        setError('');
        if (!shopName.trim()) {
            setError('Vui lòng nhập tên cửa hàng');
            return;
        }
        setStep(3);
    };

    const handleRegister = async (withSampleData, shopType) => {
        setError('');
        setIsLoading(true);
        try {
            const res = await rawApi.post('/superadmin/shops', {
                nameShop: shopName.trim(),
                nameAdmin: 'Nhập tên chủ cửa hàng',
                phoneOne: cleanedPhone,
                password: password.trim(),
                timeStart: new Date(),
                timeEnd: '2026-03-31T23:59:59',
                payed: 0,
                withSampleData,
                // Chỉ gửi ngành nghề khi tạo kèm dữ liệu mẫu
                ...(withSampleData && shopType ? { shopType } : {}),
            });

            if (res.data?.data?.account) {
                const accountInfo = {
                    userName: res.data.data.account.userName,
                    passWord: password.trim(),
                    savedAt: Date.now(),
                };
                localStorage.setItem('account', JSON.stringify(accountInfo));

                // Tự động đăng nhập — dùng rawApi để không gắn token cũ
                try {
                    const { data: loginData } = await rawApi.post('/auth/login', {
                        userName: accountInfo.userName,
                        passWord: accountInfo.passWord,
                    });
                    login(loginData);
                    // Áp cấu hình danh mục mặc định theo ngành (1 lần) cho shop vừa tạo
                    ensureShopTypeTabDefault();
                    navigate('/admin/dashboard', { replace: true });
                } catch (loginErr) {
                    const msg = loginErr.response?.data?.message;
                    setError(msg || 'Đăng ký thành công nhưng đăng nhập thất bại. Vui lòng đăng nhập lại.');
                }
            } else {
                setError('Đăng ký thành công nhưng không thể lấy thông tin tài khoản');
            }
        } catch (err) {
            const msg = err.response?.data?.message;
            setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Có lỗi xảy ra khi đăng ký');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.adminLogin}>
            {/* ===== LEFT PANEL ===== */}
            <div className={styles.adminLoginLeft}>
                {!bgLoaded && <div className={styles.adminLoginBgSkeleton} />}
                <img src={loginBg} alt="Navo Sổ Tiệm" className={styles.adminLoginBgImage} onLoad={() => setBgLoaded(true)} />
                <div className={styles.adminLoginBgOverlay} />
                <div className={styles.adminLoginLeftContent}>
                    <div className={styles.adminLoginHero} />
                    <div className={styles.adminLoginFooter}>
                        &copy; 2025 Navo Sổ Tiệm. Phiên bản: {version}
                    </div>
                </div>
            </div>

            {/* ===== RIGHT PANEL ===== */}
            <div className={styles.adminLoginRight}>
                <div className={styles.adminLoginBrand}>
                    <img src={logo} alt="Navo logo" className={styles.adminLoginBrandLogo} />
                    <span className={styles.adminLoginBrandName}>Navo Sổ Tiệm</span>
                </div>
                <button className={styles.adminLoginHomeBtn} onClick={() => navigate('/')}>
                    <House size={16} /> Trang chủ
                </button>

                <div className={styles.adminLoginFormWrapper}>
                    <h2 className={styles.adminLoginTitle}>Tạo tài khoản</h2>
                    <p className={styles.adminLoginSubtitle}>
                        {step === 1 && 'Nhập số điện thoại và mật khẩu để bắt đầu'}
                        {step === 2 && 'Nhập tên cửa hàng của bạn'}
                        {step === 3 && !showIndustry && 'Chọn cách thiết lập cửa hàng'}
                        {step === 3 && showIndustry && 'Chọn ngành nghề bạn kinh doanh'}
                    </p>

                    {/* Step indicator */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 28 }}>
                        {[1, 2, 3].map((s) => (
                            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    background: step >= s ? '#0089F6' : '#E5E7EB',
                                    color: step >= s ? '#fff' : '#9CA3AF',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 14, fontWeight: 700, transition: 'all 0.2s',
                                }}>
                                    {s}
                                </div>
                                {s < 3 && <div style={{ width: 40, height: 2, background: step > s ? '#0089F6' : '#E5E7EB', transition: 'all 0.2s' }} />}
                            </div>
                        ))}
                    </div>

                    {/* ===== STEP 1 ===== */}
                    {step === 1 && (
                        <form onSubmit={handleNextStep} className={styles.adminLoginForm}>
                            <div className={styles.adminLoginField}>
                                <label className={styles.adminLoginLabel}>SỐ ĐIỆN THOẠI</label>
                                <div className={styles.adminLoginInputWrapper}>
                                    <span className={styles.adminLoginInputIcon}><Phone /></span>
                                    <input
                                        type="tel"
                                        className={styles.adminLoginInput}
                                        placeholder="Nhập số điện thoại (10 số)"
                                        value={phone}
                                        onChange={(e) => {
                                            setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                                            setError('');
                                        }}
                                        autoComplete="tel"
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <div className={styles.adminLoginField}>
                                <label className={styles.adminLoginLabel}>MẬT KHẨU</label>
                                <div className={styles.adminLoginInputWrapper}>
                                    <span className={styles.adminLoginInputIcon}><Lock /></span>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        className={styles.adminLoginInput}
                                        placeholder="Tối thiểu 6 ký tự"
                                        value={password}
                                        onChange={(e) => { setPassword(e.target.value); setError(''); }}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className={styles.adminLoginTogglePassword}
                                        onClick={() => setShowPassword((v) => !v)}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <EyeOff /> : <Eye />}
                                    </button>
                                </div>
                            </div>

                            {error && <div className={styles.adminLoginError}>{error}</div>}

                            <button
                                type="submit"
                                className={styles.adminLoginSubmitBtn}
                                disabled={cleanedPhone.length < 10 || password.length < 6}
                            >
                                Tiếp theo <ArrowRight />
                            </button>
                        </form>
                    )}

                    {/* ===== STEP 2 ===== */}
                    {step === 2 && (
                        <form onSubmit={handleGoStep3} className={styles.adminLoginForm}>
                            <div className={styles.adminLoginField}>
                                <label className={styles.adminLoginLabel}>TÊN CỬA HÀNG</label>
                                <div className={styles.adminLoginInputWrapper}>
                                    <span className={styles.adminLoginInputIcon}><Building2 /></span>
                                    <input
                                        type="text"
                                        className={styles.adminLoginInput}
                                        placeholder="Nhập tên cửa hàng của bạn"
                                        value={shopName}
                                        onChange={(e) => { setShopName(e.target.value); setError(''); }}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            {error && <div className={styles.adminLoginError}>{error}</div>}

                            <div style={{ display: 'flex', gap: 10 }}>
                                <button
                                    type="button"
                                    onClick={() => { setStep(1); setError(''); }}
                                    style={{
                                        flex: 1, height: 52, border: '1.5px solid #E5E7EB', borderRadius: 10,
                                        background: '#fff', fontSize: 15, fontWeight: 600,
                                        cursor: 'pointer', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', gap: 6, color: '#374151',
                                    }}
                                >
                                    <ArrowLeft size={18} /> Quay lại
                                </button>
                                <button
                                    type="submit"
                                    className={styles.adminLoginSubmitBtn}
                                    style={{ flex: 2, marginTop: 0 }}
                                    disabled={!shopName.trim()}
                                >
                                    Tiếp theo <ArrowRight />
                                </button>
                            </div>
                        </form>
                    )}

                    {/* ===== STEP 3 ===== */}
                    {step === 3 && (
                        <div className={styles.adminLoginForm}>
                            {!showIndustry && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <div
                                    onClick={() => !isLoading && setShowIndustry(true)}
                                    style={{
                                        padding: '20px 24px', borderRadius: 14,
                                        border: '1.5px solid #E5E7EB', background: '#fff',
                                        cursor: isLoading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s', opacity: isLoading ? 0.6 : 1,
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0089F6'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0,137,246,0.08)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
                                        <div style={{
                                            width: 44, height: 44, borderRadius: 12, background: '#EFF6FF',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0089F6', flexShrink: 0,
                                        }}>
                                            <Sparkles size={22} />
                                        </div>
                                        <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
                                            Có dữ liệu mẫu
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.5 }}>
                                        Cửa hàng sẽ có sẵn dữ liệu mẫu giúp bạn dễ dàng dùng thử và trải nghiệm các tính năng
                                    </div>
                                </div>

                                <div
                                    onClick={() => !isLoading && handleRegister(false)}
                                    style={{
                                        padding: '20px 24px', borderRadius: 14,
                                        border: '1.5px solid #E5E7EB', background: '#fff',
                                        cursor: isLoading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s', opacity: isLoading ? 0.6 : 1,
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0089F6'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0,137,246,0.08)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
                                        <div style={{
                                            width: 44, height: 44, borderRadius: 12, background: '#F3F4F6',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280', flexShrink: 0,
                                        }}>
                                            <Database size={22} />
                                        </div>
                                        <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
                                            Không có dữ liệu mẫu
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.5 }}>
                                        Dành cho bạn đã từng sử dụng phần mềm quản lý, cửa hàng sẽ trắng toàn bộ thông tin
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* Bước con: chọn ngành nghề (khi chọn "Có dữ liệu mẫu") */}
                            {showIndustry && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                                    {BUSINESS_TYPES.map((item) => {
                                        const Icon = item.icon;
                                        return (
                                            <div
                                                key={item.key}
                                                onClick={() =>
                                                    !isLoading &&
                                                    handleRegister(true, item.key === 'khac' ? 'kinhmat' : item.key)
                                                }
                                                style={{
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                                                    gap: 8, padding: '16px 8px', borderRadius: 12,
                                                    border: '1.5px solid #E5E7EB', background: '#fff',
                                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.2s', opacity: isLoading ? 0.6 : 1, textAlign: 'center',
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#0089F6'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0,137,246,0.08)'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
                                            >
                                                <div style={{
                                                    width: 44, height: 44, borderRadius: 12, background: '#EFF6FF',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0089F6',
                                                }}>
                                                    <Icon size={22} />
                                                </div>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                                                    {item.label}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {isLoading && (
                                <div style={{ textAlign: 'center', marginTop: 16, fontSize: 14, color: '#6B7280' }}>
                                    Đang tạo cửa hàng...
                                </div>
                            )}

                            {error && <div className={styles.adminLoginError} style={{ marginTop: 16 }}>{error}</div>}

                            <button
                                type="button"
                                onClick={() => {
                                    setError('');
                                    if (showIndustry) setShowIndustry(false);
                                    else setStep(2);
                                }}
                                disabled={isLoading}
                                style={{
                                    width: '100%', height: 48, marginTop: 16,
                                    border: '1.5px solid #E5E7EB', borderRadius: 10,
                                    background: '#fff', fontSize: 15, fontWeight: 600,
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: 6, color: '#374151',
                                }}
                            >
                                <ArrowLeft size={18} /> Quay lại
                            </button>
                        </div>
                    )}

                    <div className={styles.adminLoginDivider}>
                        <span className={styles.adminLoginDividerText}>Đã có tài khoản?</span>
                    </div>

                    <button
                        className={styles.adminLoginCreateBtn}
                        onClick={() => navigate('/admin/login')}
                    >
                        Đăng nhập ngay
                    </button>

                    <p style={{ marginTop: 20, fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>
                        Hỗ trợ: <a href="tel:0886299670" style={{ color: '#0089F6', textDecoration: 'none' }}>0886.299.670</a>
                        {' '}· 8:00 – 22:00 (T2–CN)
                    </p>
                </div>
            </div>
        </div>
    );
}
